import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronRightIcon, DocumentCheckIcon, DocumentPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Note
} from '@mui/icons-material';
import { collection, addDoc, getDocs, getDoc, updateDoc, doc, query, where } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { logCaseStatusChange, logSessionReportAdded } from '../../utils/activityLogger';
import { Case, CaseClosure, MeetingFrequencyWeeks, NextStepType } from '../../types';
import { t } from '../../utils/translations';
import {
  isMeetingFrequencyWeeks,
  meetingFrequencyLabel,
} from '../../utils/meetingFrequency';
import {
  findPreviousSessionReport,
  getReportOpenNextStep,
  isOpeningSession,
  nextStoredSessionNumber,
  parseNextStepType,
  parseSessionNumber,
  toRoadSessionNumber,
} from '../SessionReports/sessionReportsUtils';
import SessionReportFormDialog from './SessionReportFormDialog';
import CaseClosureDialog, { CaseClosureFormValues } from './CaseClosureDialog';
import ExpandCollapse from '../common/ExpandCollapse';

function todayDateInputValue(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateInputLocal(value: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function isFutureDate(date: Date): boolean {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return date.getTime() > today.getTime();
}

interface SessionReportRecord {
  id: string;
  caseId: string;
  sessionNumber: number;
  meetingDate?: Date | null;
  meetingFrequencyWeeks?: MeetingFrequencyWeeks | null;
  mainTheme: string;
  personResponse: string;
  previousTaskCompleted: 'yes' | 'no' | 'partial' | null;
  previousTaskNotCompletedReason?: string;
  progressObserved: 'yes' | 'no' | 'partial' | null;
  progressNoted: string;
  progressType?: string; // spiritual, emotional, relational, attitude, action
  nextCommitments: 'yes' | 'no';
  nextCommitmentsDetails?: string;
  nextStepType?: NextStepType | null;
  nextStepPassage?: string | null;
  noCommitmentsReason?: string;
  createdAt: Date;
  createdBy: string;
  createdByName: string;
}

interface SessionReportProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  caseTitle: string;
  caseItem?: Case | null;
  onReportAdded?: (result?: {
    caseId: string;
    meetingDate: Date;
    meetingFrequencyWeeks: MeetingFrequencyWeeks | null;
    sessionNumber: number;
  }) => void;
  hideAddButton?: boolean; // Hide the "Adaugă Raport Post-Sesiune" button
  caseStatus?: string; // Case status to determine if add button should be shown
  autoOpenAddForm?: boolean; // Automatically open the add report form when dialog opens
  onCancelAddForm?: () => void; // Callback when add form is canceled
  /** Blur report bodies (admin cannot read confidential session content). */
  restrictSensitiveContent?: boolean;
  onCaseClosed?: (caseItem: Case) => void;
}

const SessionReport: React.FC<SessionReportProps> = ({
  open,
  onClose,
  caseId,
  caseTitle,
  caseItem,
  onReportAdded,
  hideAddButton = false,
  caseStatus,
  autoOpenAddForm = false,
  onCancelAddForm,
  restrictSensitiveContent = false,
  onCaseClosed,
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [addReportOpen, setAddReportOpen] = useState(false);
  const [reportTypeOpen, setReportTypeOpen] = useState(false);
  const [closureOpen, setClosureOpen] = useState(false);
  const [closureLoading, setClosureLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [reports, setReports] = useState<SessionReportRecord[]>([]);
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);
  const loadGenerationRef = useRef(0);

  // Form state
  const [sessionNumber, setSessionNumber] = useState(0);
  const [meetingDate, setMeetingDate] = useState(todayDateInputValue);
  const [meetingFrequencyWeeks, setMeetingFrequencyWeeks] = useState<MeetingFrequencyWeeks | ''>('');
  const [caseHasFrequency, setCaseHasFrequency] = useState(false);
  const [mainTheme, setMainTheme] = useState('');
  const [personResponse, setPersonResponse] = useState('');
  const [previousTaskCompleted, setPreviousTaskCompleted] = useState<'yes' | 'no' | 'partial'>('yes');
  const [previousTaskNotCompletedReason, setPreviousTaskNotCompletedReason] = useState('');
  const [progressObserved, setProgressObserved] = useState<'yes' | 'no' | 'partial'>('yes');
  const [progressNoted, setProgressNoted] = useState('');
  const [nextCommitments, setNextCommitments] = useState<'yes' | 'no'>('yes');
  const [nextCommitmentsDetails, setNextCommitmentsDetails] = useState('');
  const [nextStepType, setNextStepType] = useState<NextStepType>('scripture');
  const [nextStepPassage, setNextStepPassage] = useState('');
  const [noCommitmentsReason, setNoCommitmentsReason] = useState('');

  const storedSessionNumbers = reports.map((r) => r.sessionNumber);
  const roadSessionNumber = toRoadSessionNumber(sessionNumber, storedSessionNumbers);
  const frequencyRequired = roadSessionNumber === 0 || !caseHasFrequency;
  // Callers that jump straight into the add-report flow never show the report
  // list, so it must not flash while the reports are being fetched.
  const autoFlow = autoOpenAddForm && caseStatus === 'active';

  const loadReports = useCallback(async (isInitialLoad: boolean = false) => {
    if (!caseId) {
      setReports([]);
      setReportsLoading(false);
      return;
    }

    const generation = ++loadGenerationRef.current;
    setReportsLoading(true);
    // Clear previous case's reports immediately so they don't flash
    setReports([]);
    setExpandedReportId(null);

    try {
      const reportsRef = collection(db, 'sessionReports');
      const reportsQuery = query(reportsRef, where('caseId', '==', caseId));
      const reportsSnapshot = await getDocs(reportsQuery);

      // Ignore stale responses if another case was opened meanwhile
      if (generation !== loadGenerationRef.current) return;
      
      const reportsData: SessionReportRecord[] = [];
      reportsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const freq = data.meetingFrequencyWeeks;
        reportsData.push({
          id: docSnap.id,
          caseId: data.caseId,
          sessionNumber: parseSessionNumber(data.sessionNumber),
          meetingDate: data.meetingDate?.toDate?.() ?? null,
          meetingFrequencyWeeks: isMeetingFrequencyWeeks(freq) ? freq : null,
          mainTheme: restrictSensitiveContent ? '' : data.mainTheme,
          personResponse: restrictSensitiveContent ? '' : data.personResponse,
          previousTaskCompleted: data.previousTaskCompleted,
          previousTaskNotCompletedReason: restrictSensitiveContent
            ? ''
            : data.previousTaskNotCompletedReason || '',
          progressObserved:
            data.progressObserved === 'yes' ||
            data.progressObserved === 'no' ||
            data.progressObserved === 'partial'
              ? data.progressObserved
              : null,
          progressNoted: restrictSensitiveContent ? '' : data.progressNoted,
          nextCommitments: data.nextCommitments,
          nextCommitmentsDetails: restrictSensitiveContent
            ? ''
            : data.nextCommitmentsDetails,
          nextStepType: parseNextStepType(data.nextStepType),
          nextStepPassage: restrictSensitiveContent
            ? null
            : typeof data.nextStepPassage === 'string' && data.nextStepPassage.trim()
              ? data.nextStepPassage.trim()
              : null,
          noCommitmentsReason: restrictSensitiveContent ? '' : data.noCommitmentsReason,
          createdAt: data.createdAt.toDate(),
          createdBy: data.createdBy,
          createdByName: data.createdByName
        });
      });
      
      // Newest session first (10, 9, 8…)
      reportsData.sort((a, b) => b.sessionNumber - a.sessionNumber);
      
      setReports(reportsData);
      
      // Update session number once reports are loaded (for both autoOpenAddForm and manual opens)
      if (isInitialLoad) {
        setSessionNumber(nextStoredSessionNumber(reportsData.map((r) => r.sessionNumber)));
        if (autoFlow) {
          // Session 0 always uses the normal report. From session 1 onward,
          // let the counselor choose the normal or closure flow.
          if (reportsData.length === 0) {
            setAddReportOpen(true);
          } else {
            setReportTypeOpen(true);
          }
        }
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      if (generation === loadGenerationRef.current) {
        setReports([]);
      }
    } finally {
      if (generation === loadGenerationRef.current) {
        setReportsLoading(false);
      }
    }
  }, [caseId, restrictSensitiveContent, autoFlow]);

  const loadCaseFrequency = useCallback(async () => {
    if (!caseId) {
      setCaseHasFrequency(false);
      setMeetingFrequencyWeeks('');
      return;
    }
    try {
      const caseSnap = await getDoc(doc(db, 'cases', caseId));
      const raw = caseSnap.exists() ? caseSnap.data()?.meetingFrequencyWeeks : null;
      if (isMeetingFrequencyWeeks(raw)) {
        setCaseHasFrequency(true);
        setMeetingFrequencyWeeks(raw);
      } else {
        setCaseHasFrequency(false);
        setMeetingFrequencyWeeks('');
      }
    } catch (err) {
      console.error('Error loading case meeting frequency:', err);
      setCaseHasFrequency(false);
      setMeetingFrequencyWeeks('');
    }
  }, [caseId]);

  const resetForm = useCallback(() => {
    // Set session number to next available
    setSessionNumber(nextStoredSessionNumber(reports.map((r) => r.sessionNumber)));
    setMeetingDate(todayDateInputValue());
    setMainTheme('');
    setPersonResponse('');
    setPreviousTaskCompleted('yes');
    setPreviousTaskNotCompletedReason('');
    setProgressObserved('yes');
    setProgressNoted('');
    setNextCommitments('yes');
    setNextCommitmentsDetails('');
    setNextStepType('scripture');
    setNextStepPassage('');
    setNoCommitmentsReason('');
    void loadCaseFrequency();
  }, [reports, loadCaseFrequency]);

  const openAddReportFlow = () => {
    setSessionNumber(nextStoredSessionNumber(reports.map((r) => r.sessionNumber)));
    if (reports.length === 0) {
      // The opening session is always a standard session report.
      setAddReportOpen(true);
      return;
    }
    setReportTypeOpen(true);
  };

  useEffect(() => {
    if (open && caseId) {
      setMeetingDate(todayDateInputValue());
      setMainTheme('');
      setPersonResponse('');
      setPreviousTaskCompleted('yes');
      setPreviousTaskNotCompletedReason('');
      setProgressObserved('yes');
      setProgressNoted('');
      setNextCommitments('yes');
      setNextCommitmentsDetails('');
      setNextStepType('scripture');
      setNextStepPassage('');
      setNoCommitmentsReason('');
      setExpandedReportId(null);

      void loadReports(true);
      void loadCaseFrequency();
    } else if (!open) {
      setAddReportOpen(false);
      setReportTypeOpen(false);
      setClosureOpen(false);
      setExpandedReportId(null);
      setReports([]);
      setReportsLoading(false);
      setCaseHasFrequency(false);
      setMeetingFrequencyWeeks('');
    }
  }, [open, caseId, autoOpenAddForm, caseStatus, loadReports, loadCaseFrequency]);

  const handleAddReport = async () => {
    if (loading) return;

    const parsedMeetingDate = parseDateInputLocal(meetingDate);
    if (!parsedMeetingDate) {
      setSnackbar({ open: true, message: t.sessionReports.meetingDateRequired, severity: 'error' });
      return;
    }
    if (isFutureDate(parsedMeetingDate)) {
      setSnackbar({ open: true, message: t.sessionReports.meetingDateFuture, severity: 'error' });
      return;
    }

    if (frequencyRequired && !isMeetingFrequencyWeeks(meetingFrequencyWeeks)) {
      setSnackbar({ open: true, message: t.sessionReports.meetingFrequencyRequired, severity: 'error' });
      return;
    }

    // Validate required fields
    const openingSession = isOpeningSession(sessionNumber, storedSessionNumbers);
    if (
      !mainTheme.trim() ||
      !personResponse.trim() ||
      (!openingSession && !progressNoted.trim())
    ) {
      setSnackbar({ open: true, message: 'Toate câmpurile sunt obligatorii', severity: 'error' });
      return;
    }
    
    // Validate reason field when previousTaskCompleted is 'partial' or 'no'
    if (
      !openingSession &&
      (previousTaskCompleted === 'partial' || previousTaskCompleted === 'no') &&
      !previousTaskNotCompletedReason.trim()
    ) {
      // Same message as the wizard's — from the one key, so the wording cannot drift apart again.
      setSnackbar({
        open: true,
        message: t.sessionReports.wizard.previousTaskReasonRequired,
        severity: 'error',
      });
      return;
    }

    if (nextCommitments === 'yes' && !nextCommitmentsDetails.trim()) {
      setSnackbar({
        open: true,
        message: t.sessionReports.wizard.commitmentsDetailsRequired,
        severity: 'error',
      });
      return;
    }

    if (nextCommitments === 'no' && !noCommitmentsReason.trim()) {
      setSnackbar({
        open: true,
        message: t.sessionReports.wizard.noCommitmentsReasonRequired,
        severity: 'error',
      });
      return;
    }

    try {
      setLoading(true);

      const frequencyToSave = isMeetingFrequencyWeeks(meetingFrequencyWeeks)
        ? meetingFrequencyWeeks
        : null;

      const passageTrimmed = nextStepPassage.trim();
      const detailsTrimmed = nextCommitmentsDetails.trim();
      
      const reportData: Record<string, unknown> = {
        caseId,
        sessionNumber,
        meetingDate: parsedMeetingDate,
        mainTheme: mainTheme.trim(),
        personResponse: personResponse.trim(),
        previousTaskCompleted: openingSession ? null : previousTaskCompleted,
        previousTaskNotCompletedReason:
          !openingSession &&
          (previousTaskCompleted === 'partial' || previousTaskCompleted === 'no')
            ? previousTaskNotCompletedReason.trim()
            : '',
        progressObserved: openingSession ? null : progressObserved,
        progressNoted: openingSession ? '' : progressNoted.trim(),
        nextCommitments,
        nextCommitmentsDetails: nextCommitments === 'yes' ? detailsTrimmed : '',
        nextStepType: nextCommitments === 'yes' ? nextStepType : null,
        nextStepPassage:
          nextCommitments === 'yes' && passageTrimmed ? passageTrimmed : null,
        noCommitmentsReason: nextCommitments === 'no' ? noCommitmentsReason.trim() : '',
        createdAt: new Date(),
        createdBy: currentUser?.id,
        createdByName: currentUser?.fullName || ''
      };

      if (frequencyToSave != null) {
        reportData.meetingFrequencyWeeks = frequencyToSave;
      }

      await addDoc(collection(db, 'sessionReports'), reportData);

      const openNextStep =
        nextCommitments === 'yes'
          ? {
              type: nextStepType,
              description: detailsTrimmed,
              passage: passageTrimmed || null,
              fromSessionNumber: roadSessionNumber,
            }
          : null;

      const caseUpdate: Record<string, unknown> = {
        lastMeetingDate: parsedMeetingDate,
        lastTheme: mainTheme.trim(),
        openNextStep,
        // Opens the ask to clarify THIS session — set here rather than on the session-closed
        // prompt's buttons so it survives every way of leaving that prompt (picking "Acum" and
        // closing without saving, or never answering it at all). It supersedes any earlier
        // session's still-open ask, and is answered by saving that session's tablou.
        pendingClarifySession: sessionNumber,
        updatedAt: new Date(),
      };
      if (frequencyToSave != null) {
        caseUpdate.meetingFrequencyWeeks = frequencyToSave;
      }
      await updateDoc(doc(db, 'cases', caseId), caseUpdate);
      if (frequencyToSave != null) {
        setCaseHasFrequency(true);
      }
      
      // Log the activity
      if (currentUser?.id && currentUser?.fullName) {
        await logSessionReportAdded(
          caseId,
          caseTitle,
          toRoadSessionNumber(sessionNumber, storedSessionNumbers),
          currentUser.id,
          currentUser.fullName
        );
      }
      
      setSnackbar({ open: true, message: 'Raportul a fost adăugat cu succes', severity: 'success' });
      resetForm();
      setAddReportOpen(false);
      loadReports(false); // Reload reports without auto-opening form
      
      // Close the dialog and trigger callback to close all modals
      if (onReportAdded) {
        onReportAdded({
          caseId,
          meetingDate: parsedMeetingDate,
          meetingFrequencyWeeks: frequencyToSave,
          sessionNumber,
        });
      } else {
        // If no callback, just close the dialog
        onClose();
      }
    } catch (error) {
      console.error('Error adding session report:', error);
      setSnackbar({ open: true, message: 'Eroare la adăugarea raportului', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddClosureReport = async (values: CaseClosureFormValues) => {
    if (!caseItem || !currentUser || closureLoading || reports.length === 0) return;

    try {
      setClosureLoading(true);
      const closedAt = new Date();
      const createdByName =
        currentUser.fullName || currentUser.email || 'Unknown User';
      const closure: CaseClosure = {
        ...values,
        // Session 0 is intentionally unavailable in this flow.
        sessionNumber: values.sessionNumber && values.sessionNumber > 0
          ? values.sessionNumber
          : reports.length,
        closedAt,
        createdBy: currentUser.id,
        createdByName,
      };
      const updatedAt = new Date();

      await updateDoc(doc(db, 'cases', caseItem.id), {
        status: closure.status,
        closure,
        updatedAt,
      });

      if (caseItem.status !== closure.status) {
        try {
          await logCaseStatusChange(
            caseItem.id,
            caseItem.title,
            caseItem.status,
            closure.status,
            currentUser.id,
            createdByName
          );
        } catch (activityError) {
          // The case closure is already safely stored; activity history is secondary.
          console.error('Error logging case closure:', activityError);
        }
      }

      const updatedCase: Case = {
        ...caseItem,
        status: closure.status,
        closure,
        updatedAt,
      };
      setClosureOpen(false);
      setSnackbar({
        open: true,
        message: t.caseClosure.saveSuccess,
        severity: 'success',
      });
      onCaseClosed?.(updatedCase);
      if (!onCaseClosed) onClose();
    } catch (error) {
      console.error('Error adding closure report:', error);
      setSnackbar({
        open: true,
        message: t.caseClosure.saveError,
        severity: 'error',
      });
    } finally {
      setClosureLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    setAddReportOpen(false);
    setReportTypeOpen(false);
    setClosureOpen(false);
    setExpandedReportId(null);
    onClose();
  };

  return (
    <>
      {open && autoFlow && reportsLoading && (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center p-4"
          role="status"
          aria-live="polite"
        >
          <div className="absolute inset-0 bg-slate-900/40" aria-hidden />
          <div className="relative flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-lg">
            <CircularProgress size={18} sx={{ color: '#C99700' }} />
            <span className="text-sm text-slate-600">Se încarcă rapoartele…</span>
          </div>
        </div>
      )}

      <Dialog
        open={open && !autoFlow && !addReportOpen && !reportTypeOpen && !closureOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Note sx={{ color: '#ffc700' }} />
            {caseTitle}
          </Box>
        </DialogTitle>
        <DialogContent>
              {!hideAddButton && caseStatus === 'active' && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    onClick={openAddReportFlow}
                    sx={{ 
                      backgroundColor: '#ffc700',
                      color: '#000',
                      fontWeight: 'bold',
                      '&:hover': { backgroundColor: '#e6b300' }
                    }}
                  >
                    {t.sessionReports.addReport}
                  </Button>
                </Box>
              )}

              {/* Info message about session reports */}
              <Alert severity="info" sx={{ mb: 3 }}>
                Raportul post-sesiune este completat după fiecare întâlnire de consiliere pentru a urmări progresul 
                și pentru a menține continuitatea între sesiuni.
              </Alert>

              {reportsLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    py: 6,
                  }}
                  role="status"
                  aria-live="polite"
                >
                  <CircularProgress size={32} sx={{ color: '#C99700' }} />
                  <Typography variant="body2" color="text.secondary">
                    Se încarcă rapoartele…
                  </Typography>
                </Box>
              ) : reports.length > 0 ? (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Rapoarte Post-Sesiune ({reports.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reports.map((report) => (
                  <Card 
                    key={report.id} 
                    variant="outlined"
                    sx={{ 
                      cursor: restrictSensitiveContent ? 'default' : 'pointer',
                      '&:hover': restrictSensitiveContent ? undefined : { boxShadow: 2 },
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={
                      restrictSensitiveContent
                        ? undefined
                        : () =>
                            setExpandedReportId(
                              expandedReportId === report.id ? null : report.id
                            )
                    }
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Raport Sesiune {toRoadSessionNumber(report.sessionNumber, storedSessionNumbers)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {report.meetingDate
                              ? `${report.meetingDate.toLocaleDateString('ro-RO')} · ${report.createdByName}`
                              : `${report.createdAt.toLocaleDateString('ro-RO')} - ${report.createdByName}`}
                          </Typography>
                          {restrictSensitiveContent ? (
                            <Box sx={{ position: 'relative', mt: 1.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  filter: 'blur(6px)',
                                  userSelect: 'none',
                                  pointerEvents: 'none',
                                }}
                                aria-hidden
                              >
                                Conținut confidențial al raportului de sesiune. Detaliile nu sunt
                                disponibile pentru acest rol.
                              </Typography>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    bgcolor: 'background.paper',
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    boxShadow: 1,
                                    textAlign: 'center',
                                  }}
                                >
                                  Conținut confidențial — vizibil doar pentru lideri.
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <ExpandCollapse open={expandedReportId !== report.id}>
                              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
                                Tema: {report.mainTheme}
                              </Typography>
                            </ExpandCollapse>
                          )}
                        </Box>
                      </Box>

                      {!restrictSensitiveContent && (
                        <ExpandCollapse open={expandedReportId === report.id}>
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Detalii Raport
                          </Typography>

                          {report.meetingDate && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {t.sessionReports.meetingDate}:
                              </Typography>
                              <Typography variant="body2">
                                {report.meetingDate.toLocaleDateString('ro-RO')}
                              </Typography>
                            </Box>
                          )}

                          {report.meetingFrequencyWeeks && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {t.sessionReports.meetingFrequency}:
                              </Typography>
                              <Typography variant="body2">
                                {meetingFrequencyLabel(report.meetingFrequencyWeeks)}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              1. Subiectul principal abordat:
                            </Typography>
                            <Typography variant="body2">{report.mainTheme}</Typography>
                          </Box>

                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              2. Răspuns persoana consiliată:
                            </Typography>
                            <Typography variant="body2">{report.personResponse}</Typography>
                          </Box>

                          {!isOpeningSession(report.sessionNumber, storedSessionNumbers) && (
                          <>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              3. Tema/pașii anteriori împliniți:
                            </Typography>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {report.previousTaskCompleted === 'yes' ? 'Da' : 
                               report.previousTaskCompleted === 'partial' ? 'Parțial' : 'Nu'}
                            </Typography>
                            {(report.previousTaskCompleted === 'partial' || report.previousTaskCompleted === 'no') && report.previousTaskNotCompletedReason && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic', pl: 2 }}>
                                Motiv: {report.previousTaskNotCompletedReason}
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              4. Progres observat:
                            </Typography>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {report.progressObserved === 'yes'
                                ? 'Da'
                                : report.progressObserved === 'partial'
                                  ? 'Parțial'
                                  : report.progressObserved === 'no'
                                    ? 'Nu'
                                    : null}
                            </Typography>
                            {report.progressNoted && (
                              <Typography
                                variant="body2"
                                color={report.progressObserved ? 'text.secondary' : undefined}
                                sx={{
                                  mt: report.progressObserved ? 0.5 : 0,
                                  fontStyle: report.progressObserved ? 'italic' : undefined,
                                  pl: report.progressObserved ? 2 : 0,
                                }}
                              >
                                {report.progressNoted}
                              </Typography>
                            )}
                          </Box>
                          </>
                          )}

                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              5. Angajamente pentru următoarea întâlnire:
                            </Typography>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {report.nextCommitments === 'yes' ? 'Da' : 'Nu'}
                            </Typography>
                            {report.nextCommitments === 'yes' && (
                              <>
                                {(report.nextStepType || report.nextStepPassage) && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {[
                                      report.nextStepType
                                        ? t.sessionReports.wizard.nextStepTypes[report.nextStepType]
                                        : null,
                                      report.nextStepPassage,
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </Typography>
                                )}
                                {report.nextCommitmentsDetails && (
                                  <Typography variant="body2" sx={{ mt: 0.5, ml: 1, fontStyle: 'italic' }}>
                                    {report.nextCommitmentsDetails}
                                  </Typography>
                                )}
                              </>
                            )}
                            {report.nextCommitments === 'no' && report.noCommitmentsReason && (
                              <Typography variant="body2" sx={{ mt: 0.5, ml: 1, fontStyle: 'italic' }}>
                                Motiv: {report.noCommitmentsReason}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        </ExpandCollapse>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
              ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Închide</Button>
        </DialogActions>
      </Dialog>

      {reportTypeOpen && (
        <div
          className="fixed inset-0 z-[1400] flex items-stretch justify-center sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-type-title"
        >
          <div
            className="absolute inset-0 bg-slate-900/40"
            aria-hidden="true"
            onClick={() => {
              setReportTypeOpen(false);
              onCancelAddForm?.();
            }}
          />
          {/* Same box as the rest of the report flow (wizard, session-closed prompt) — a
              chooser that resizes mid-flow reads as leaving the flow, not a step in it. */}
          <div className="safe-pt safe-pb relative flex h-full w-full flex-col overflow-hidden bg-white shadow-lg sm:h-[min(90vh,38rem)] sm:max-w-lg sm:rounded-xl sm:border sm:border-slate-200">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-4 pb-3 pt-4 sm:px-5 sm:pt-5">
              <div className="min-w-0">
                <h2 id="report-type-title" className="text-base font-semibold text-slate-900">
                  {t.caseClosure.reportTypeTitle}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {t.caseClosure.reportTypeSubtitle}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setReportTypeOpen(false);
                  onCancelAddForm?.();
                }}
                aria-label={t.common.close}
                className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              <ul className="space-y-2">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setReportTypeOpen(false);
                      setAddReportOpen(true);
                    }}
                    className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left transition duration-200 ease-out hover:border-brand-300 hover:bg-brand-50 active:scale-[0.99]"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                      <DocumentPlusIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-slate-900">
                        {t.caseClosure.normalReport}
                      </span>
                      <span className="block text-sm text-slate-500">
                        {t.caseClosure.normalReportDescription}
                      </span>
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                </li>

                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setReportTypeOpen(false);
                      setClosureOpen(true);
                    }}
                    className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left transition duration-200 ease-out hover:border-brand-300 hover:bg-brand-50 active:scale-[0.99]"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                      <DocumentCheckIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-slate-900">
                        {t.caseClosure.finalReport}
                      </span>
                      <span className="block text-sm text-slate-500">
                        {t.caseClosure.finalReportDescription}
                      </span>
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                </li>
              </ul>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-slate-100 px-4 py-3 sm:px-5">
              <button
                type="button"
                onClick={() => {
                  setReportTypeOpen(false);
                  onCancelAddForm?.();
                }}
                className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {t.common.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      <SessionReportFormDialog
        open={addReportOpen}
        caseTitle={caseTitle}
        loading={loading}
        frequencyRequired={frequencyRequired}
        previousNextStep={(() => {
          const previousReport = findPreviousSessionReport(reports, sessionNumber);
          return previousReport
            ? getReportOpenNextStep(previousReport, storedSessionNumbers)
            : null;
        })()}
        values={{
          sessionNumber,
          meetingDate,
          meetingFrequencyWeeks,
          mainTheme,
          personResponse,
          previousTaskCompleted,
          previousTaskNotCompletedReason,
          progressObserved,
          progressNoted,
          nextCommitments,
          nextCommitmentsDetails,
          nextStepType,
          nextStepPassage,
          noCommitmentsReason,
        }}
        onChange={(patch) => {
          if (patch.sessionNumber !== undefined) setSessionNumber(patch.sessionNumber);
          if (patch.meetingDate !== undefined) setMeetingDate(patch.meetingDate);
          if (patch.meetingFrequencyWeeks !== undefined) {
            setMeetingFrequencyWeeks(patch.meetingFrequencyWeeks);
          }
          if (patch.mainTheme !== undefined) setMainTheme(patch.mainTheme);
          if (patch.personResponse !== undefined) setPersonResponse(patch.personResponse);
          if (patch.previousTaskCompleted !== undefined) {
            setPreviousTaskCompleted(patch.previousTaskCompleted);
          }
          if (patch.previousTaskNotCompletedReason !== undefined) {
            setPreviousTaskNotCompletedReason(patch.previousTaskNotCompletedReason);
          }
          if (patch.progressObserved !== undefined) setProgressObserved(patch.progressObserved);
          if (patch.progressNoted !== undefined) setProgressNoted(patch.progressNoted);
          if (patch.nextCommitments !== undefined) setNextCommitments(patch.nextCommitments);
          if (patch.nextCommitmentsDetails !== undefined) {
            setNextCommitmentsDetails(patch.nextCommitmentsDetails);
          }
          if (patch.nextStepType !== undefined) setNextStepType(patch.nextStepType);
          if (patch.nextStepPassage !== undefined) setNextStepPassage(patch.nextStepPassage);
          if (patch.noCommitmentsReason !== undefined) {
            setNoCommitmentsReason(patch.noCommitmentsReason);
          }
        }}
        onClose={() => {
          if (loading) return;
          setAddReportOpen(false);
          if (onCancelAddForm) onCancelAddForm();
        }}
        existingSessionNumbers={storedSessionNumbers}
        onSubmit={() => {
          void handleAddReport();
        }}
      />

      <CaseClosureDialog
        open={closureOpen}
        caseItem={caseItem ?? null}
        reportCount={reports.length}
        loading={closureLoading}
        useNextSession
        onClose={() => {
          if (closureLoading) return;
          setClosureOpen(false);
          setReportTypeOpen(true);
        }}
        onSubmit={(values) => {
          void handleAddClosureReport(values);
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SessionReport;

